// Insight types as returned by the API's `insight_type` field.
export const INSIGHT_TYPE = {
  AGENT: 'call_center',
  EXECUTIVE: 'operational',
  AUTOMATION: 'automation',
}

// Within the Insights tab, insights split into two audiences: agent insights
// (on-call, launchable as real-time guidance) and executive insights
// (off-call process/training fixes for the team). The tab shows one at a time
// via a sub-toggle, defaulting to agent.
export const INSIGHT_KIND = {
  AGENT: 'agent',
  EXECUTIVE: 'executive',
}

export const INSIGHT_KIND_ORDER = [INSIGHT_KIND.AGENT, INSIGHT_KIND.EXECUTIVE]

export const INSIGHT_KIND_LABEL = {
  [INSIGHT_KIND.AGENT]: 'Agent',
  [INSIGHT_KIND.EXECUTIVE]: 'Executive',
}

// The three top-level views. Each answers a different question:
//   Insights  = Act      (the sparse, actionable recommendations)
//   Experiments = Test   (recommendations that were launched and are being measured)
//   Explore   = Understand (the full breakdown of how calls mapped to opportunities)
export const VIEW = {
  INSIGHTS: 'insights',
  EXPERIMENTS: 'experiments',
  EXPLORE: 'explore',
}

export const VIEW_ORDER = [VIEW.INSIGHTS, VIEW.EXPERIMENTS, VIEW.EXPLORE]

export const VIEW_LABEL = {
  [VIEW.INSIGHTS]: 'Insights',
  [VIEW.EXPERIMENTS]: 'Experiments',
  [VIEW.EXPLORE]: 'Explore',
}

// Fill-in-the-blank lead-ins for the goal → obstacle → recommendation spine.
// GOAL is phrased to read grammatically with the outcome-phrased opportunity
// names the pipeline produces today ("New appointments booked"). FIX says
// "say" because `recommendation` is currently the verbatim agent line;
// TODO(backend): once a short generated `recommendation_summary` and a
// goal-phrased opportunity name are persisted per run, revert FIX to
// "…we recommend agents…" and GOAL to "…opportunity to…".
export const SPINE_LEAD = {
  GOAL: 'On calls where the opportunity is…',
  OBSTACLE: "One of the main reasons they don't convert…",
  // FIX precedes the verbatim script ("…say…"); FIX_SUMMARY precedes the
  // generated tactic description, which the agent does rather than says.
  FIX: 'To win more of them, we recommend agents say…',
  FIX_SUMMARY: 'To win more of them, we recommend agents…',
  // Executive insights are off-call process/training fixes, so their
  // recommendation is aimed at the team, not an on-call agent line.
  FIX_EXEC: 'To fix this, we recommend your team…',
}

// A reason a call didn't convert is one of two kinds. Objections are
// customer-side and addressable by the agent on the call; operational issues
// happen off the call (process/back-office) and aren't something an agent can
// change mid-conversation. Mirrors the API `conversion_reason_type` and the
// agent (call_center) vs executive (operational) insight split.
export const REASON_TYPE = {
  OBJECTION: 'objection',
  OPERATIONAL: 'operational',
}

export const REASON_TYPE_LABEL = {
  [REASON_TYPE.OBJECTION]: 'Customer objections',
  [REASON_TYPE.OPERATIONAL]: 'Operational issues',
}

// The kinds of deep-dive the shared drawer can show.
export const DRAWER_KIND = {
  INSIGHT: 'insight',
  EXECUTIVE: 'executive',
  REASON: 'reason',
  INCONCLUSIVE: 'inconclusive',
}

// Scope selector: today a "run" is scoped to a single playbook, but the concept
// is a run (could later be org / agent-subset / combination). ALL groups every
// playbook's run into separate sections with no cross-run totals.
export const SCOPE_ALL = 'all'

// Insights older than this drop off the page even when they're still their
// filter set's newest run. Clears stragglers from old custom-filter runs that no
// longer correspond to a current playbook.
export const INSIGHT_MAX_AGE_DAYS = 60

// Dates are shown as the day they were recorded — U.S. format, no time, no zone.
export const DATE_ONLY_FORMAT = 'MM/dd/yyyy'

// Distinct reasons the Insights tab can be empty. Each gets its own copy and,
// eventually, its own backend trigger. TODO(backend): today the API can't tell
// these apart at runtime — it just returns a (possibly empty) insight list.
export const EMPTY_STATE = {
  NOTHING_ACTIONABLE: 'nothingActionable',
  ALL_LAUNCHED: 'allLaunched',
  FIRST_RUN: 'firstRun',
  LOW_VOLUME: 'lowVolume',
  // The scope has insights, but its newest run is past INSIGHT_MAX_AGE_DAYS.
  EXPIRED: 'expired',
  // Everything left to act on was dismissed by the viewer.
  ALL_DISMISSED: 'allDismissed',
}

// The experiment scope hasn't come back yet — distinct from `null`, which is a
// request that failed. Launch waits for the first and proceeds on the second, so
// they can't share a value.
export const SCOPE_LOADING = 'scopeLoading'

// Why the insights list couldn't be loaded. `NOT_ENABLED` and `NOT_PERMITTED`
// are ordinary places to land rather than failures — the org dropdown lists every
// org a Balto admin can see, whether or not the feature is on and whether or not
// they may currently query it — so they get plain copy and no toast.
export const INSIGHTS_ERROR = {
  NOT_ENABLED: 'notEnabled',
  NOT_PERMITTED: 'notPermitted',
  FETCH_FAILED: 'fetchFailed',
}

// Experiment statuses that mean "launched and being measured" (Experiments tab)
// vs "not launched yet" (Insights tab). `pending` means a dynamic prompt was
// created but the experiment never started (see TREN-356) — still not launched.
export const LAUNCHED_EXPERIMENT_STATUSES = ['running', 'succeeded', 'failed', 'cancelled']
export const RUNNING_EXPERIMENT_STATUSES = ['running']
export const COMPLETED_EXPERIMENT_STATUSES = ['succeeded', 'failed', 'cancelled']

// Dismissing hides a never-launched insight for the whole org; setting it back
// to pending restores it. Neither deletes anything.
export const PENDING_EXPERIMENT_STATUS = 'pending'
export const DISMISSED_EXPERIMENT_STATUS = 'dismissed'
